/**
 * Admin User Directory — Full user management for Platform Super Admin
 * Clickable role cards, searchable directory, user actions
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import {
  Users, Building2, Home, UserCheck, Search, Eye, EyeOff,
  Shield, ShieldCheck, Ban, RefreshCw, Key, Loader2,
  ChevronRight, Clock, Mail, Phone, MapPin, X, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_CONFIG = {
  platform_super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-800', icon: ShieldCheck },
  admin: { label: 'Admin', color: 'bg-red-100 text-red-700', icon: Shield },
  hoa_admin: { label: 'HOA Admin', color: 'bg-blue-100 text-blue-800', icon: Building2 },
  landlord: { label: 'Landlord', color: 'bg-emerald-100 text-emerald-800', icon: Home },
  property_manager: { label: 'Property Manager', color: 'bg-purple-100 text-purple-800', icon: Users },
  tenant: { label: 'Tenant', color: 'bg-yellow-100 text-yellow-800', icon: UserCheck },
  accountant: { label: 'Accountant', color: 'bg-teal-100 text-teal-800', icon: Users },
  support_staff: { label: 'Support', color: 'bg-gray-100 text-gray-700', icon: Users }
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800',
  disabled: 'bg-gray-100 text-gray-600'
};

const UserDirectory = ({ stats, onRefresh }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showResetPw, setShowResetPw] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [activities, setActivities] = useState([]);

  const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

  useEffect(() => { fetchUsers(); fetchActivity(); }, [roleFilter]);

  const fetchUsers = async (searchTerm = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter) params.append('role', roleFilter);
      if (searchTerm) params.append('search', searchTerm);
      const res = await axios.get(`${API}/api/admin/users?${params}`, { headers });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    }
    setLoading(false);
  };

  const fetchActivity = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/activity-feed?limit=15`, { headers });
      setActivities(res.data.activities || []);
    } catch (e) { /* silent */ }
  };

  const viewUser = async (user) => {
    setSelectedUser(user);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/users/${user.id}`, { headers });
      setUserDetail(res.data);
    } catch (e) {
      setUserDetail({ user, properties: [], units: [], tenants: [], login_history: [] });
    }
    setDetailLoading(false);
  };

  const suspendUser = async (userId) => {
    if (!window.confirm('Suspend this user? They will lose access immediately.')) return;
    try {
      await axios.post(`${API}/api/admin/users/${userId}/suspend`, {}, { headers });
      toast.success('User suspended');
      fetchUsers(search);
      setSelectedUser(null);
      if (onRefresh) onRefresh();
    } catch (e) { toast.error('Failed to suspend'); }
  };

  const activateUser = async (userId) => {
    try {
      await axios.post(`${API}/api/admin/users/${userId}/activate`, {}, { headers });
      toast.success('User activated');
      fetchUsers(search);
      setSelectedUser(null);
      if (onRefresh) onRefresh();
    } catch (e) { toast.error('Failed to activate'); }
  };

  const resetPassword = async () => {
    if (newPassword.length < 8) { toast.error('Min 8 characters'); return; }
    try {
      await axios.post(`${API}/api/admin/users/${showResetPw.id}/reset-password`, { password: newPassword }, { headers, 'Content-Type': 'application/json' });
      toast.success('Password reset');
      setShowResetPw(null);
      setNewPassword('');
    } catch (e) { toast.error('Failed to reset password'); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(search);
  };

  const counts = stats?.user_counts || {};

  const roleCards = [
    { key: 'property_manager', label: 'Property Managers', count: counts.property_managers || 0, color: 'border-purple-200 bg-purple-50', textColor: 'text-purple-700' },
    { key: 'hoa_admin', label: 'HOA Communities', count: counts.hoa_communities || 0, color: 'border-blue-200 bg-blue-50', textColor: 'text-blue-700' },
    { key: 'landlord', label: 'Landlords', count: counts.landlords || 0, color: 'border-emerald-200 bg-emerald-50', textColor: 'text-emerald-700' },
    { key: '', label: 'Total Users', count: total || counts.total_users || 0, color: 'border-gray-200 bg-gray-50', textColor: 'text-gray-700' }
  ];

  return (
    <div className="space-y-4" data-testid="user-directory">
      {/* Clickable Role Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {roleCards.map(card => (
          <Card
            key={card.label}
            className={`cursor-pointer transition-all hover:shadow-md ${card.color} ${roleFilter === card.key ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => { setRoleFilter(roleFilter === card.key ? '' : card.key); }}
            data-testid={`role-card-${card.key || 'all'}`}
          >
            <CardContent className="p-4">
              <p className="text-sm font-medium text-gray-600">{card.label}</p>
              <p className={`text-2xl font-bold ${card.textColor}`}>{card.count}</p>
              <p className="text-xs text-gray-500">{roleFilter === card.key ? 'Click to clear filter' : 'Click to filter'}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-3">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email, organization, property..."
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>
            <Button type="submit" size="sm" data-testid="user-search-btn">
              <Search className="h-4 w-4 mr-1" /> Search
            </Button>
            {(search || roleFilter) && (
              <Button type="button" size="sm" variant="outline" onClick={() => { setSearch(''); setRoleFilter(''); fetchUsers(); }}>
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            )}
          </form>
          {roleFilter && (
            <div className="mt-2 flex items-center gap-2">
              <Filter className="h-3 w-3 text-blue-600" />
              <Badge className="bg-blue-100 text-blue-800 text-xs">Filtered: {roleCards.find(r => r.key === roleFilter)?.label}</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2"><Users className="h-4 w-4 text-blue-600" /> User Directory ({total})</span>
            <Button size="sm" variant="outline" onClick={() => fetchUsers(search)}>
              <RefreshCw className="h-3 w-3 mr-1" /> Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No users found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-y">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Name / Email</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Role</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Organization</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Properties</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Status</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Created</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map(user => {
                    const rc = ROLE_CONFIG[user.role] || ROLE_CONFIG.tenant;
                    return (
                      <tr key={user.id} className="hover:bg-gray-50" data-testid={`user-row-${user.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900 truncate max-w-[200px]">{user.name || 'N/A'}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[200px]">{user.email}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={`text-[10px] ${rc.color}`}>{rc.label}</Badge>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-xs truncate max-w-[150px]">{user.org_name || '—'}</div>
                          {user.org_type && <div className="text-[10px] text-gray-400 capitalize">{user.org_type}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {user.properties_count > 0 ? (
                            <span>{user.properties_count}P / {user.units_count}U / {user.tenants_count}T</span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={`text-[10px] ${STATUS_COLORS[user.status] || 'bg-gray-100'}`}>
                            {user.status || 'active'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => viewUser(user)} data-testid={`view-user-${user.id}`}>
                              <Eye className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setShowResetPw(user); setNewPassword(''); }}>
                              <Key className="h-3 w-3" />
                            </Button>
                            {user.status === 'active' ? (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-600 border-red-200" onClick={() => suspendUser(user.id)}>
                                <Ban className="h-3 w-3" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-green-600 border-green-200" onClick={() => activateUser(user.id)}>
                                <RefreshCw className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600" /> Recent User Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activities.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Activity will appear here as users log in and perform actions</p>
          ) : (
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {activities.map((a, i) => (
                <div key={i} className="flex items-center gap-3 p-2 border rounded text-xs">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${a.activity_type === 'login' ? 'bg-green-100' : 'bg-blue-100'}`}>
                    {a.activity_type === 'login' ? <UserCheck className="h-3 w-3 text-green-600" /> : <Shield className="h-3 w-3 text-blue-600" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="font-medium">{a.user_email || a.email || 'Unknown'}</span>
                    <span className="text-gray-500"> — {a.action || 'login'}</span>
                    {a.user_role && <Badge className="ml-1 text-[9px] bg-gray-100">{a.user_role}</Badge>}
                  </div>
                  <span className="text-gray-400 shrink-0">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setUserDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              {selectedUser?.name || selectedUser?.email}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : userDetail && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-xs text-gray-500 flex items-center gap-1"><Mail className="h-3 w-3" /> Email</p>
                  <p className="font-medium text-sm">{userDetail.user?.email}</p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-xs text-gray-500 flex items-center gap-1"><Shield className="h-3 w-3" /> Role</p>
                  <Badge className={ROLE_CONFIG[userDetail.user?.role]?.color || 'bg-gray-100'}>
                    {ROLE_CONFIG[userDetail.user?.role]?.label || userDetail.user?.role}
                  </Badge>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-xs text-gray-500 flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</p>
                  <p className="font-medium text-sm">{userDetail.user?.phone || userDetail.user?.phone_number || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Country</p>
                  <p className="font-medium text-sm">{userDetail.user?.country || 'N/A'}</p>
                </div>
              </div>

              {/* Organization */}
              {userDetail.organization && (
                <div className="border rounded p-3">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2"><Building2 className="h-4 w-4 text-blue-600" /> Organization</h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><span className="text-gray-500">Name:</span> {userDetail.organization.name}</div>
                    <div><span className="text-gray-500">Type:</span> <span className="capitalize">{userDetail.organization.type}</span></div>
                    <div><span className="text-gray-500">Status:</span> <Badge className={STATUS_COLORS[userDetail.organization.status] || 'bg-gray-100'}>{userDetail.organization.status}</Badge></div>
                  </div>
                </div>
              )}

              {/* Hierarchy */}
              {userDetail.hierarchy && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <h4 className="font-medium text-sm mb-2">Hierarchy</h4>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <Badge className="bg-white border">{userDetail.hierarchy.user}</Badge>
                    <ChevronRight className="h-3 w-3 text-gray-400" />
                    <Badge className="bg-white border">{userDetail.hierarchy.role}</Badge>
                    {userDetail.hierarchy.organization && (
                      <><ChevronRight className="h-3 w-3 text-gray-400" /><Badge className="bg-white border">{userDetail.hierarchy.organization}</Badge></>
                    )}
                    <ChevronRight className="h-3 w-3 text-gray-400" />
                    <Badge className="bg-white border">{userDetail.hierarchy.properties_count} Properties</Badge>
                    <ChevronRight className="h-3 w-3 text-gray-400" />
                    <Badge className="bg-white border">{userDetail.hierarchy.units_count} Units</Badge>
                    <ChevronRight className="h-3 w-3 text-gray-400" />
                    <Badge className="bg-white border">{userDetail.hierarchy.tenants_count} Tenants</Badge>
                  </div>
                </div>
              )}

              {/* Properties */}
              {userDetail.properties && userDetail.properties.length > 0 && (
                <div className="border rounded p-3">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2"><Home className="h-4 w-4 text-emerald-600" /> Properties ({userDetail.properties.length})</h4>
                  <div className="space-y-1">
                    {userDetail.properties.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                        <span className="font-medium">{p.name || p.address || `Property ${i+1}`}</span>
                        <span className="text-gray-500">{p.units_count || 0} units</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tenants */}
              {userDetail.tenants && userDetail.tenants.length > 0 && (
                <div className="border rounded p-3">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2"><UserCheck className="h-4 w-4 text-yellow-600" /> Tenants ({userDetail.tenants.length})</h4>
                  <div className="space-y-1">
                    {userDetail.tenants.slice(0, 10).map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                        <span>{t.first_name} {t.last_name}</span>
                        <span className="text-gray-500">{t.email}</span>
                      </div>
                    ))}
                    {userDetail.tenants.length > 10 && <p className="text-xs text-gray-400 text-center">...and {userDetail.tenants.length - 10} more</p>}
                  </div>
                </div>
              )}

              {/* Login History */}
              {userDetail.login_history && userDetail.login_history.length > 0 && (
                <div className="border rounded p-3">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2"><Clock className="h-4 w-4 text-gray-600" /> Login History</h4>
                  <div className="space-y-1">
                    {userDetail.login_history.map((l, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-1.5 border-b last:border-0">
                        <span>{new Date(l.timestamp).toLocaleString()}</span>
                        <span className="text-gray-500">{l.ip_address || 'N/A'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="border-t pt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => { setShowResetPw(selectedUser); setNewPassword(''); }}>
                  <Key className="h-3 w-3 mr-1" /> Reset Password
                </Button>
                {selectedUser?.status === 'active' ? (
                  <Button size="sm" variant="destructive" onClick={() => suspendUser(selectedUser.id)}>
                    <Ban className="h-3 w-3 mr-1" /> Suspend
                  </Button>
                ) : (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => activateUser(selectedUser.id)}>
                    <RefreshCw className="h-3 w-3 mr-1" /> Activate
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!showResetPw} onOpenChange={() => setShowResetPw(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reset Password — {showResetPw?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">New Password (min 8 characters)</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" />
                <button className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowResetPw(null)}>Cancel</Button>
            <Button size="sm" onClick={resetPassword} disabled={newPassword.length < 8}>Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDirectory;
